import { computed, ref } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { fitWorkspaceListWidth } from 'dashboard/helper/workspaceV2';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { useOptimisticSettings } from 'dashboard/composables/useOptimisticSetting';
import { listWidthTransitionClass as listWidthTransitionClassFor } from 'dashboard/composables/useFocusMotion';
import wootConstants from 'dashboard/constants/globals';

const LIST_AVATAR = 64;
const LIST_COMPACT = 256;
const LIST_COMFORTABLE = 340;
// Encaixe largo, pedido do Lucas em 21/08: no confortavel (340) as abas
// "Nao lidas / Pendentes / Minhas" ainda nao cabiam ("o menu mais aberto
// possivel come a parte do pendentes"). O padrao continua sendo encaixes,
// nunca largura livre; so ganhou um degrau a mais.
const LIST_WIDE = 480;
const LIST_HIDE_AT = 40;
const LIST_AVATAR_AT = 160;
const LIST_COMPACT_AT = 300;
// Teto do arrasto. Era 420, o que deixava pouca folga para quem quer a lista
// larga de verdade num monitor grande.
const LIST_MAX = 520;
const RIGHT_OPEN = 320;
const RIGHT_CLOSE_AT = 160;

const listDragWidth = ref(null);
const rightDragWidth = ref(null);
const isListDragging = ref(false);
const listDragPreviewPx = ref(null);

let listStartX = 0;
let listStartW = 0;
let rightStartX = 0;
let rightStartW = 0;
let draggingList = false;
let draggingRight = false;
let listenersBound = false;
let snapListWidthFn = () => {};
let snapRightWidthFn = () => {};

const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

// O arrasto NÃO é largura livre: ele encaixa ao vivo num dos tamanhos
// configurados (esconder, avatares 64, compacta 256, confortável 340,
// larga 480).
// Pedido do Lucas (18/08): "1 linha e conforme arrasta vai setando em um dos
// 3 tamanhos". Largura contínua durou um dia (v839/v840) e confundia — o
// encaixe diz de cara onde a lista vai parar quando soltar.
const LIST_SNAP_SIZES = [
  LIST_AVATAR,
  LIST_COMPACT,
  LIST_COMFORTABLE,
  LIST_WIDE,
];
const listSnapGuides = LIST_SNAP_SIZES;

export const WORKSPACE_LIST_RESIZE_LEVELS = [
  { key: 'icons', width: LIST_AVATAR },
  { key: 'compact', width: LIST_COMPACT },
  { key: 'comfortable', width: LIST_COMFORTABLE },
  { key: 'wide', width: LIST_WIDE },
];

const snapListPx = raw => {
  if (raw < LIST_HIDE_AT) return 0;
  return LIST_SNAP_SIZES.reduce((melhor, tamanho) =>
    Math.abs(tamanho - raw) < Math.abs(melhor - raw) ? tamanho : melhor
  );
};

const onPointerMove = event => {
  if (draggingList) {
    const delta = getClientX(event) - listStartX;
    const raw = Math.max(0, Math.min(LIST_MAX, listStartW + delta));
    // So o indicador composto acompanha o dedo. Mexer em listDragWidth
    // aqui forca layout das 3 colunas a cada pointermove (0 frames).
    listDragPreviewPx.value = raw;
    if (event.cancelable) event.preventDefault();
  }
  if (draggingRight) {
    const delta = rightStartX - getClientX(event);
    rightDragWidth.value = Math.max(0, Math.min(400, rightStartW + delta));
    if (event.cancelable) event.preventDefault();
  }
};

const onPointerUp = () => {
  if (!draggingList && !draggingRight) return;
  if (draggingList) {
    const raw = listDragPreviewPx.value ?? listStartW;
    const px = snapListPx(raw);
    listDragPreviewPx.value = null;
    snapListWidthFn(px);
    // Com a camada otimista o uiSettings reflete no MESMO tick, entao o
    // resquicio do arrasto pode (e deve) morrer aqui. Deixa-lo vivo era o
    // que sombreava a escolha de modo: depois de qualquer arrasto,
    // Lista/So fotos/Foco total paravam de responder, porque isListHidden
    // e isAvatarRail viam o listDragWidth antigo e ignoravam o modo salvo.
    listDragWidth.value = null;
  }
  if (draggingRight) {
    snapRightWidthFn(rightDragWidth.value ?? 0);
    rightDragWidth.value = null;
  }
  draggingList = false;
  draggingRight = false;
  isListDragging.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });
};

const cancelListDrag = () => {
  if (!draggingList) return;
  draggingList = false;
  isListDragging.value = false;
  listDragWidth.value = null;
  listDragPreviewPx.value = null;
  if (typeof document !== 'undefined' && !draggingRight) {
    Object.assign(document.body.style, { cursor: '', userSelect: '' });
  }
};

const bindDragListeners = () => {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;
  document.addEventListener('mousemove', onPointerMove);
  document.addEventListener('mouseup', onPointerUp);
  document.addEventListener('touchmove', onPointerMove, { passive: false });
  document.addEventListener('touchend', onPointerUp);
};

export function useWorkbenchLayout() {
  const { isWorkspaceV2 } = useWorkspaceV2();
  const rawSettings = useUISettings();
  const { uiSettings, updateUISettings } = useOptimisticSettings(
    rawSettings.uiSettings,
    rawSettings.updateUISettings
  );
  const { isFocusModeEnabled } = useFocusMode();
  const { width: windowWidth } = useWindowSize();

  bindDragListeners();

  // Mesmo corte do `md:` do Tailwind: SMALL_SCREEN_BREAKPOINT (768).
  // Abaixo disso a lista é a tela inteira: largura de rail é coisa de desktop.
  const isOnSmallScreen = computed(
    () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
  );

  const listMode = computed(() => {
    const mode = uiSettings.value?.workbench_list_mode;
    if (mode === 'avatars' || mode === 'hidden' || mode === 'list') return mode;
    // Sem escolha gravada a lista aparece: esconder é decisão de quem atende,
    // tomada no cluster arrastável, nunca um padrão herdado.
    return 'list';
  });

  // Largura livre escolhida no arrasto. Antes o Modo Foco só sabia escolher
  // entre hidden/avatars/list, e "list" caía sempre em compacto de 256px:
  // arrastar para alargar não tinha efeito nenhum depois de soltar.
  const savedListWidth = computed(() => {
    const raw = Number(uiSettings.value?.workbench_list_width);
    if (!Number.isFinite(raw) || raw < LIST_AVATAR_AT) return null;
    // Normaliza largura livre gravada na era v839/v840 para o encaixe mais
    // próximo — quem ficou com 386px salvo passa a abrir em 340.
    return snapListPx(Math.min(raw, LIST_MAX));
  });

  // Durante o gesto a coluna fica no ponto de partida (listDragWidth nao
  // acompanha o dedo). O indicador composto usa listDragPreviewPx.
  const listWidthPx = computed(() => {
    // SiteUp: no celular a lista ocupa a janela inteira. A largura do rail
    // (arrasto, encaixe 64/256/340) é de desktop: se vazar para cá, o
    // ancestral grid trava em pixel e sobra faixa morta à direita (medido
    // em staging: janela 375, lista 319).
    if (isOnSmallScreen.value) return null;
    const desired =
      listDragWidth.value != null ? listDragWidth.value : savedListWidth.value;
    if (!isWorkspaceV2.value || desired == null || desired < LIST_AVATAR_AT)
      return desired;
    return fitWorkspaceListWidth(
      desired,
      windowWidth.value,
      uiSettings.value.workspace_v2_navigation,
      uiSettings.value.is_contact_sidebar_open === true
    );
  });

  const isListCompact = computed(() => {
    // SiteUp: no celular a lista É a tela inteira, então nunca compacta. Sem
    // esta guarda ela herdava a preferência salva no desktop e o telefone
    // nascia só com ícones, sem as abas (Não lidas / Pendentes / Minhas), que
    // são a navegação principal ali.
    if (isOnSmallScreen.value) return false;
    // Com largura escolhida, quem manda é ela: é assim que alargar traz os
    // rótulos de volta em vez de manter os ícones para sempre.
    if (listWidthPx.value != null) return listWidthPx.value < LIST_COMPACT_AT;
    if (isFocusModeEnabled.value) return true;
    return uiSettings.value?.workbench_list_compact === true;
  });

  const isAvatarRail = computed(() => {
    if (listDragWidth.value != null) {
      return (
        listDragWidth.value > LIST_HIDE_AT &&
        listDragWidth.value < LIST_AVATAR_AT
      );
    }
    return isFocusModeEnabled.value && listMode.value === 'avatars';
  });

  const isListHidden = computed(() => {
    if (listDragWidth.value != null && listDragWidth.value > LIST_HIDE_AT) {
      return false;
    }
    return isFocusModeEnabled.value && listMode.value === 'hidden';
  });

  const isRightOpen = computed(
    () => uiSettings.value?.is_contact_sidebar_open === true
  );

  const setListMode = mode => {
    // Escolher um modo no menu e um ato explicito: nenhum resquicio de
    // arrasto pode falar mais alto que ele.
    listDragWidth.value = null;
    listDragPreviewPx.value = null;
    const patch = {
      workbench_list_mode: mode,
      workbench_list_compact: true,
    };
    // Esconder é reversível: guardamos de onde saímos para que reaparecer
    // devolva a escolha de quem atende. Sem isso, quem trabalha no rail de
    // avatares perde o rail a cada ciclo de esconder e mostrar.
    if (mode === 'hidden' && listMode.value !== 'hidden') {
      patch.workbench_list_mode_before_hide = listMode.value;
    }
    return updateUISettings(patch);
  };

  const snapListWidth = px => {
    if (isFocusModeEnabled.value) {
      // UM patch atomico, nunca dois: largura e modo em chamadas separadas
      // correm entre si — cada resposta devolve o objeto inteiro e a que
      // chega por ultimo ressuscita a chave velha da outra. Na tela: soltar
      // no estreito gravava width 0 mas o modo voltava a "list", e a lista
      // assentava numa largura fantasma (medido 329px na v846).
      // A limpeza vai como 0, nunca null: o merge por chave descarta nulls.
      const patch = { workbench_list_compact: true };
      if (px < LIST_HIDE_AT) {
        patch.workbench_list_width = 0;
        patch.workbench_list_mode = 'hidden';
        if (listMode.value !== 'hidden') {
          patch.workbench_list_mode_before_hide = listMode.value;
        }
      } else if (px < LIST_AVATAR_AT) {
        patch.workbench_list_width = 0;
        patch.workbench_list_mode = 'avatars';
      } else {
        patch.workbench_list_width = Math.round(px);
        patch.workbench_list_mode = 'list';
      }
      updateUISettings(patch);
      return;
    }
    updateUISettings({
      workbench_list_width: Math.round(px),
      workbench_list_compact: px < LIST_COMPACT_AT,
    });
  };

  const snapRightWidth = px => {
    updateUISettings({ is_contact_sidebar_open: px >= RIGHT_CLOSE_AT });
  };

  snapListWidthFn = snapListWidth;
  snapRightWidthFn = snapRightWidth;

  const toggleListCompact = () => {
    if (isFocusModeEnabled.value) {
      if (listMode.value === 'hidden') {
        const previous = uiSettings.value?.workbench_list_mode_before_hide;
        setListMode(previous === 'avatars' ? 'avatars' : 'list');
        return;
      }
      setListMode('hidden');
      return;
    }
    updateUISettings({
      workbench_list_compact: !uiSettings.value?.workbench_list_compact,
    });
  };

  const listWidthTransitionClass = computed(() =>
    listWidthTransitionClassFor({ dragging: isListDragging.value })
  );

  const listSnapTarget = computed(() =>
    listDragPreviewPx.value == null ? null : snapListPx(listDragPreviewPx.value)
  );

  const listAvailableWidth = computed(() =>
    fitWorkspaceListWidth(
      LIST_WIDE,
      windowWidth.value,
      uiSettings.value.workspace_v2_navigation,
      uiSettings.value.is_contact_sidebar_open === true
    )
  );
  const listResizeWidth = computed(() => {
    if (isListHidden.value) return 0;
    if (isAvatarRail.value) return LIST_AVATAR;
    if (listWidthPx.value != null) return snapListPx(listWidthPx.value);
    return isListCompact.value ? LIST_COMPACT : LIST_COMFORTABLE;
  });

  const setListResizeWidth = width => {
    if (!LIST_SNAP_SIZES.includes(width)) return;
    snapListWidth(width);
  };

  const hideConversationList = () => {
    snapListWidth(0);
  };

  const startListDrag = event => {
    draggingList = true;
    isListDragging.value = true;
    listStartX = getClientX(event);
    if (isListHidden.value) {
      listStartW = 0;
    } else if (isAvatarRail.value) {
      listStartW = LIST_AVATAR;
    } else if (savedListWidth.value != null) {
      // Continuar de onde parou; sem isso o segundo arrasto saltava de volta
      // para o preset e desfazia a largura escolhida antes.
      listStartW = savedListWidth.value;
    } else if (isListCompact.value) {
      listStartW = LIST_COMPACT;
    } else {
      listStartW = LIST_COMFORTABLE;
    }
    listDragWidth.value = listStartW;
    listDragPreviewPx.value = listStartW;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  const startRightDrag = event => {
    draggingRight = true;
    rightStartX = getClientX(event);
    rightStartW = isRightOpen.value ? RIGHT_OPEN : 0;
    rightDragWidth.value = rightStartW;
    Object.assign(document.body.style, {
      cursor: 'col-resize',
      userSelect: 'none',
    });
    event.preventDefault();
  };

  return {
    listMode,
    isListCompact,
    isAvatarRail,
    isListHidden,
    isRightOpen,
    isOnSmallScreen,
    listDragWidth,
    listWidthPx,
    listWidthTransitionClass,
    isListDragging,
    listDragPreviewPx,
    listSnapTarget,
    listSnapGuides,
    listResizeWidth,
    listAvailableWidth,
    rightDragWidth,
    setListMode,
    setListResizeWidth,
    hideConversationList,
    cancelListDrag,
    toggleListCompact,
    startListDrag,
    startRightDrag,
  };
}
